import { ChangeEvent, useState } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faTrashCan,
  faSpinner,
  faArrowUpRightFromSquare,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import storageUploadPrefix from "../../../helpers/storageUploadPrefix";
import { csrfTokenHeader } from "../../../helpers/reactQueryApi";
import UploadBar from "../../shared/fields/fileUploadField/UploadBar";
import FileIcon from "../../shared/FileIcon";

// Kontextdokument-Upload für einen tixxtAI-Prompt (#4842). Lädt eine einzelne
// PDF/DOCX/TXT/Markdown nach file_server_ex v2 (storageDirectory "files"),
// validiert Typ + Größe (≤2 MB) clientseitig und meldet die file_id (die echte
// FS::File-_id aus der Upload-Antwort) über onChange an den Stimulus-Controller
// (versteckte Form-Felder). Wichtig: file_server_ex ignoriert die clientseitig
// gesendete external_id bei Session-/Browser-Uploads, daher referenzieren wir
// die Datei über die zurückgelieferte id (wie useFileUpload). Der extrahierte
// Text wird serverseitig async erzeugt (PDF/DOCX via Docling, TXT/Markdown
// direkt gelesen); für persistierte Prompts pollt das Widget den
// Verarbeitungsstatus ("Fortschritt bis zur Verfügbarkeit").

const ALLOWED_EXTENSIONS = ["pdf", "docx", "txt", "md", "markdown"];
const PLAINTEXT_EXTENSIONS = ["txt", "md", "markdown"];
const MAX_SIZE = 2 * 1024 * 1024;
// Clientseitige Fallback-Konstante für den Sofort-Check von Klartextdateien;
// die Antwort des Server-Checks (echtes, env-konfigurierbares Limit) gewinnt.
const CONTEXT_CHAR_LIMIT = 50000;
const STORAGE_DIRECTORY = "files";
// Frontend-Keys brauchen den js.-Prefix (nur js.* wird ans Frontend ausgeliefert).
const I18N_PREFIX = "js.administration.network_settings.ai_services.document";
// Bekannte Warnungs-/Fehlercodes des Server-Checks — unbekannte Codes (z.B.
// von einer neueren Server-Version) werden ignoriert statt kaputt gerendert.
const WARNING_KEYS = ["too_long", "no_text", "encrypted", "unreadable"];

const formatNumber = (value: number | null | undefined) => {
  if (value === null || value === undefined) return "";
  try {
    // de_DU ist kein valider BCP-47-Tag → "de-DU"; toLocaleString wirft bei
    // ungültigen Tags, daher der Fallback auf die rohe Zahl.
    return value.toLocaleString(I18n.currentLocale().replace("_", "-"));
  } catch {
    return String(value);
  }
};

const warningMessage = (
  key: string | null | undefined,
  chars?: number | null,
  limit?: number | null,
) => {
  if (!key || !WARNING_KEYS.includes(key)) return null;
  return I18n.t(`${I18N_PREFIX}.warnings.${key}`, {
    chars: formatNumber(chars),
    limit: formatNumber(limit ?? CONTEXT_CHAR_LIMIT),
  });
};

type Phase =
  | "empty"
  | "uploading"
  | "pending_save"
  | "persisted"
  | "upload_failed";

type StatusResponse = {
  state: string | null;
  filename: string | null;
  error: string | null;
  chars?: number | null;
  limit?: number | null;
  warning?: string | null;
};

type Props = {
  initialFileId?: string;
  initialFilename?: string;
  initialState?: string;
  statusUrl?: string;
  checkUrl?: string;
  onChange: (fileId: string | null, filename: string | null) => void;
};

export default function PromptDocumentField({
  initialFileId,
  initialFilename,
  initialState,
  statusUrl,
  checkUrl,
  onChange,
}: Props) {
  const [phase, setPhase] = useState<Phase>(
    initialFileId ? "persisted" : "empty",
  );
  const [fileId, setFileId] = useState<string | null>(initialFileId || null);
  const [filename, setFilename] = useState<string | null>(
    initialFilename || null,
  );
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Unverbindlicher Hinweis aus dem Zeichen-Check (zu lang / Scan ohne Text /
  // verschlüsselt / unlesbar) — blockiert das Speichern nie.
  const [warning, setWarning] = useState<string | null>(null);

  // Direkter Zugriffslink auf die hochgeladene Datei (#4842). Zeigt inline
  // (z.B. PDF) oder lädt herunter; autorisiert über die Session. Nur für
  // persistierte Dokumente: der /get-Endpoint liefert ausschließlich
  // published Dateien aus, und publish passiert erst beim Speichern des
  // Prompts — vor dem Save würde der Link 404en (Datei noch volatile).
  const extensionOf = (name: string | null) =>
    (name?.split(".").pop() || "").toLowerCase();
  const documentUrl = (id: string, name: string | null) =>
    `/api/storage/files/${id}/get/${encodeURIComponent(name || "")}`;

  // Live-Status der serverseitigen Extraktion — nur für bereits persistierte
  // Dokumente (auf einer frisch geladenen Bearbeitungsseite). Stoppt, sobald
  // der Status terminal ist.
  const { data: status } = useQuery<StatusResponse>(
    ["ai-prompt-document-status", statusUrl],
    async () => (await axios.get(statusUrl as string)).data,
    {
      enabled: !!statusUrl && phase === "persisted",
      initialData: initialState
        ? {
            state: initialState,
            filename: initialFilename || null,
            error: null,
          }
        : undefined,
      refetchInterval: (data) => (data?.state === "processing" ? 2000 : false),
    },
  );

  // Zeichen-Check parallel zum Storage-Upload (#4842): Klartext sofort im
  // Browser zählen, dann die rohe Datei an den foundation-Check-Endpoint
  // schicken (extrahiert PDF/DOCX serverseitig ohne Docling). Nur beratend —
  // jeder Fehler hier wird verschluckt und blockiert den Upload nie.
  async function checkDocument(file: File) {
    const extension = extensionOf(file.name);
    if (PLAINTEXT_EXTENSIONS.includes(extension)) {
      try {
        const text = await file.text();
        if (text.length > CONTEXT_CHAR_LIMIT) {
          setWarning(
            warningMessage("too_long", text.length, CONTEXT_CHAR_LIMIT),
          );
        }
      } catch {
        // Lesefehler — der Server-Check unten übernimmt.
      }
    }
    if (!checkUrl) return;
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: check } = await axios.post(checkUrl, data, {
        headers: csrfTokenHeader(),
      });
      setWarning(warningMessage(check.warning, check.chars, check.limit));
    } catch {
      // Advisory only.
    }
  }

  async function uploadFile(file: File) {
    setError(null);
    setWarning(null);

    const extension = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setError(I18n.t(`${I18N_PREFIX}.errors.invalid_type`));
      return;
    }
    if (file.size > MAX_SIZE) {
      setError(I18n.t(`${I18N_PREFIX}.errors.too_large`));
      return;
    }

    void checkDocument(file);

    const data = new FormData();
    data.append("file", file);
    data.append("name", file.name);
    data.append("type", file.type);
    // external_id wird bei Session-/Browser-Uploads von file_server_ex ignoriert;
    // wir referenzieren die Datei stattdessen über die zurückgelieferte id.
    data.append("external_id", uuidv4());

    setPhase("uploading");
    setProgress(0);
    setFilename(file.name);

    try {
      const response = await axios.post(
        `${storageUploadPrefix(STORAGE_DIRECTORY)}/${STORAGE_DIRECTORY}`,
        data,
        {
          onUploadProgress: ({ loaded, total }) => {
            if (total) setProgress((loaded * 100) / total);
          },
        },
      );
      const uploadedId = response.data?.id;
      if (!uploadedId) {
        setPhase("upload_failed");
        setError(I18n.t(`${I18N_PREFIX}.errors.upload_failed`));
        return;
      }
      setFileId(uploadedId);
      setPhase("pending_save");
      onChange(uploadedId, file.name);
    } catch (e: any) {
      setPhase("upload_failed");
      setError(
        e?.response?.status === 413
          ? I18n.t(`${I18N_PREFIX}.errors.too_large`)
          : I18n.t(`${I18N_PREFIX}.errors.upload_failed`),
      );
    }
  }

  function removeDocument() {
    setPhase("empty");
    setFileId(null);
    setFilename(null);
    setProgress(0);
    setError(null);
    setWarning(null);
    onChange(null, null);
  }

  function onSelect(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) uploadFile(file);
  }

  const picker = (labelKey: string) => (
    <label className="btn btn-light btn-sm cursor-pointer mb-0">
      <FontAwesomeIcon icon={faUpload} className="me-1" />
      {I18n.t(`${I18N_PREFIX}.${labelKey}`)}
      <input
        type="file"
        className="hidden"
        accept=".pdf,.docx,.txt,.md,.markdown"
        onChange={onSelect}
      />
    </label>
  );

  const removeButton = (
    <button
      type="button"
      className="btn btn-sm btn-light text-danger"
      onClick={removeDocument}
    >
      <FontAwesomeIcon icon={faTrashCan} className="me-1" />
      {I18n.t(`${I18N_PREFIX}.remove`)}
    </button>
  );

  // Dateiname als Zugriffslink mit typspezifischem Datei-Icon (wie im
  // Datei-Upload, FileIcon). Öffnet die Datei in einem neuen Tab.
  const documentLink = fileId && (
    <a
      href={documentUrl(fileId, filename)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm fw-bold hover:underline"
      title={filename || undefined}
    >
      <FileIcon extension={extensionOf(filename)} className="text-lg" />
      <span className="truncate max-w-xs">{filename}</span>
      <FontAwesomeIcon
        icon={faArrowUpRightFromSquare}
        className="text-xs text-muted"
      />
    </a>
  );

  // Vor dem Speichern nur Name + Icon ohne Link: die Datei ist noch volatile
  // und der /get-Endpoint würde 404en (siehe documentUrl-Kommentar oben).
  const documentName = (
    <span
      className="inline-flex items-center gap-1 text-sm fw-bold"
      title={filename || undefined}
    >
      <FileIcon extension={extensionOf(filename)} className="text-lg" />
      <span className="truncate max-w-xs">{filename}</span>
    </span>
  );

  const statusBadge = () => {
    const state = status?.state;
    if (state === "ready") {
      return (
        <span className="badge bg-success">
          {I18n.t(`${I18N_PREFIX}.status.ready`)}
        </span>
      );
    }
    if (state === "failed") {
      return (
        <span className="badge bg-danger">
          {I18n.t(`${I18N_PREFIX}.status.failed`)}
        </span>
      );
    }
    return (
      <span className="badge bg-secondary">
        <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
        {I18n.t(`${I18N_PREFIX}.status.processing`)}
      </span>
    );
  };

  // Warnung/Fehlerdetail für bereits gespeicherte Dokumente aus dem gepollten
  // Status ableiten — so überlebt der "zu lang"-Hinweis den Seiten-Reload.
  const statusWarning =
    phase === "persisted" && status?.state === "ready"
      ? warningMessage(status.warning, status.chars, status.limit)
      : null;
  const statusFailureDetail =
    phase === "persisted" && status?.state === "failed"
      ? warningMessage(status.error, status.chars, status.limit)
      : null;
  const visibleWarning = warning || statusWarning;

  return (
    <div className="border-box p-3">
      {phase === "empty" && picker("select")}

      {phase === "uploading" && (
        <div className="flex flex-col gap-1">
          <span className="text-sm">{filename}</span>
          <div className="progress border-box">
            <UploadBar progress={progress} state="uploading" />
          </div>
        </div>
      )}

      {phase === "pending_save" && (
        <div className="flex items-center gap-2 flex-wrap">
          {documentName}
          <span className="text-sm text-muted">
            {I18n.t(`${I18N_PREFIX}.pending_save`)}
          </span>
          {removeButton}
        </div>
      )}

      {phase === "persisted" && (
        <div className="flex items-center gap-2 flex-wrap">
          {documentLink}
          {statusBadge()}
          {removeButton}
        </div>
      )}

      {phase === "upload_failed" && (
        <div className="flex items-center gap-2 flex-wrap">
          {picker("select")}
        </div>
      )}

      {error && <div className="text-danger text-sm mt-1">{error}</div>}
      {!error && statusFailureDetail && (
        <div className="text-danger text-sm mt-1">{statusFailureDetail}</div>
      )}
      {visibleWarning && (
        <div className="text-amber-700 text-sm mt-1">
          <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
          {visibleWarning}
        </div>
      )}
    </div>
  );
}
